import {
  type MediaAsset,
  type MediaUrl,
  type Paginated,
  type PlatformMediaAsset,
  type PlatformMediaListQuerySchema,
  type PresignUploadRequest,
  type PresignedUpload,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The wire shape — `z.input`, so the page/pageSize defaults are optional here. */
type PlatformMediaListParams = z.input<typeof PlatformMediaListQuerySchema>;

/**
 * How long a minted read url may sit in the cache **unused** before RTK Query
 * drops it, in seconds.
 *
 * Deliberately a small constant rather than a read of `media.readUrlTtlSeconds`:
 * `shared/` may not import `modules/Settings` (W2), and guessing high is the one
 * failure that matters here — a cached url outliving its expiry renders as a
 * broken thumbnail nobody can explain (N25). It is a ceiling on staleness, not
 * the freshness rule: `useAdminMediaSource` re-mints from each response's own
 * `expiresAt`, which is the authority.
 */
const URL_CACHE_SECONDS = 30;

/**
 * The **platform's own** media, as the portal sees it (P10.2e's routes).
 *
 * `shared/` rather than a module because no module owns it: `brand.logoMediaId`
 * is edited from Settings today, and any later `media`-controlled setting — or a
 * brand-asset surface of its own — reads exactly the same library. This is the
 * media counterpart of the `*-lookup.api` bridges (W6).
 *
 * The library list carries each row's url inline (`PlatformMediaAsset`), because
 * a grid of thumbnails minted one HTTP call at a time is N+1 requests for a page.
 *
 * The **platform library** is resolved through the list endpoint, never through
 * the mint below. `GET /media/:id/url` answers a url and nothing else, so a
 * stored `brand.logoMediaId` resolved that way would show its picture and lose
 * its dimensions and size on every reload; the ONE stored id goes through this
 * list's `ids` filter instead (`useAdminMediaSource.useAsset`), which is also
 * why one tag invalidates everything.
 *
 * `readMediaUrl` is the other half, added by P15a for **member-owned evidence**,
 * and it is a genuinely different question — see its own note.
 */
export const mediaApi = createApi({
  reducerPath: 'mediaApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PlatformMediaList'],
  endpoints: (builder) => ({
    listPlatformMedia: builder.query<Paginated<PlatformMediaAsset>, PlatformMediaListParams>({
      query: (params) => ({ url: '/admin/media', method: 'GET', params }),
      providesTags: ['PlatformMediaList'],
      // The rows carry urls that expire; a page held for the RTK default of
      // sixty seconds would be a grid of dead links by the time it is reopened.
      keepUnusedDataFor: URL_CACHE_SECONDS,
    }),

    /**
     * One short-lived, authorized read url for an asset the **member** owns —
     * how a reviewer sees an ID document or a selfie (P15a).
     *
     * **Not `/admin/media`.** That surface is the platform's own library: it is
     * authorized by `brand.read` and refuses every member-owned kind outright,
     * so an `id_document` cannot be resolved through it at all. `GET /media/:id/url`
     * is deliberately the same route a member uses for their own photo, because
     * "who may read this asset" depends on the asset's KIND — evidence needs
     * `verification.review` — and one authorization path is what keeps a
     * reviewer and a member from diverging.
     *
     * **The api writes an audit row before it mints** (N12): every staff view of
     * identity evidence is recorded, and a view that could not be recorded is
     * refused rather than served. So a call here is not a free read — it is the
     * act the audit trail describes, and nothing should fire it speculatively.
     *
     * `keepUnusedDataFor` is the same staleness ceiling the library uses, and it
     * is only a ceiling: `useEvidenceMediaSource` re-mints from each response's
     * own `expiresAt`, which is the authority (N25). Deliberately **untagged** —
     * a mint is not a cacheable resource anything invalidates, it is a
     * capability with an expiry.
     */
    readMediaUrl: builder.query<MediaUrl, string>({
      query: (mediaId) => ({ url: `/media/${mediaId}/url`, method: 'GET' }),
      keepUnusedDataFor: URL_CACHE_SECONDS,
    }),

    presignPlatformUpload: builder.mutation<PresignedUpload, PresignUploadRequest>({
      query: (body) => ({ url: '/admin/media/presign-upload', method: 'POST', body }),
    }),

    /**
     * Step 3 of the upload (the PUT is step 2, and it does not go through RTK
     * Query — it is a signed request to object storage, not to the api).
     *
     * The body is an empty object: the admin finalize refuses `sortOrder`, which
     * is member vocabulary for the arrangement of a photo grid.
     */
    finalizePlatformUpload: builder.mutation<MediaAsset, string>({
      query: (mediaId) => ({ url: `/admin/media/${mediaId}/finalize`, method: 'POST', body: {} }),
      invalidatesTags: ['PlatformMediaList'],
    }),

    deletePlatformMedia: builder.mutation<void, string>({
      query: (mediaId) => ({ url: `/admin/media/${mediaId}`, method: 'DELETE' }),
      /**
       * The list tag is the whole invalidation, and it is enough.
       *
       * `useAdminMediaSource.useAsset` resolves the ONE stored id through this
       * same list endpoint (the `ids` filter), so a delete takes the row off
       * every grid *and* flips the picker holding it to "unavailable" — with no
       * event, no subscriber and no second source of truth. A `media.asset.changed`
       * domain event was written here first and removed: W5's bus exists for
       * CROSS-module reactivity, and nothing outside this one shared api reads
       * platform media.
       */
      invalidatesTags: ['PlatformMediaList'],
    }),
  }),
});

export const {
  useListPlatformMediaQuery,
  useReadMediaUrlQuery,
  usePresignPlatformUploadMutation,
  useFinalizePlatformUploadMutation,
  useDeletePlatformMediaMutation,
} = mediaApi;
